import { useState, useMemo, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useIntegrationStatus } from "@/hooks/use-integration-status";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { 
  Search, 
  Package, 
  Clock, 
  CheckCircle,
  AlertTriangle,
  Download,
  Filter,
  RefreshCw,
  ExternalLink,
  Factory,
  FileText,
  Calendar,
  DollarSign,
  MapPin,
  User,
  ClipboardList,
  ArrowRight,
  Printer,
  Plus,
  Zap,
  Settings2,
  X,
  PlayCircle,
  PauseCircle,
  StopCircle,
  Wrench,
  Package2,
  BarChart3
} from "lucide-react";
import { toast } from "sonner";
import { useNavigation } from "@/components/layout/app-layout";
import { useContextualHelp } from "@/hooks/use-contextual-help";
import { HelpButton } from "@/components/help-button";
import { ContextualHelpDrawer } from "@/components/contextual-help-drawer";
import { AdvancedFiltersModal } from "@/components/advanced-filters-modal";
import { FilterRow, FilterLogic, ActiveFilterChip } from "@/types/filters";
import { applyFilters, generateFilterChips } from "@/lib/filter-utils";
import { ConfigureColumnsModal, ColumnDefinition, ColumnConfig } from "@/components/configure-columns-modal";
import {
  exportToCSV,
  exportToExcel,
  getCurrentDateForFilename,
} from "@/lib/export-utils";
import { StaticMetricCardAlt } from "@/components/StaticMetricCardAlt";

// Types
interface WorkOrderMaterial {
  id: string;
  lineNumber: number;
  stockItemCode: string;
  stockItemName: string;
  description: string;
  quantityRequired: number;
  quantityIssued: number;
  quantityOutstanding: number;
  unitCost: number;
  totalCost: number;
  warehouseCode?: string;
  warehouseName?: string;
}

interface WorkOrderOperation {
  id: string;
  operationNumber: number;
  operationName: string;
  workCenter: string;
  description: string;
  setupTime: number; // minutes
  runTime: number; // minutes per unit
  totalTime: number; // minutes
  status: "pending" | "in-progress" | "completed" | "skipped";
  startDate?: Date;
  endDate?: Date;
  assignedTo?: string;
}

interface WorkOrder {
  id: string;
  workOrderNumber: string;
  productCode: string;
  productName: string;
  description: string;
  quantityOrdered: number;
  quantityCompleted: number;
  quantityOutstanding: number;
  status: "planned" | "released" | "in-progress" | "completed" | "on-hold" | "cancelled";
  priority: "low" | "normal" | "high" | "urgent";
  startDate: Date;
  dueDate: Date;
  completionDate?: Date;
  bomVersion: string;
  routingVersion: string;
  projectCode?: string;
  projectName?: string;
  warehouseCode: string;
  warehouseName: string;
  estimatedCost: number;
  actualCost: number;
  currency: string;
  notes: string;
  materials: WorkOrderMaterial[];
  operations: WorkOrderOperation[];
  createdBy: string;
  createdDate: Date;
  lastModifiedBy: string;
  lastModifiedDate: Date;
}

// Mock data
const MOCK_WORK_ORDERS: WorkOrder[] = [
  {
    id: "1",
    workOrderNumber: "WO-2024-001",
    productCode: "FG-001",
    productName: "Assembled Widget Pro",
    description: "Standard production run for Widget Pro",
    quantityOrdered: 100,
    quantityCompleted: 65,
    quantityOutstanding: 35,
    status: "in-progress",
    priority: "high",
    startDate: new Date("2024-01-15"),
    dueDate: new Date("2024-02-15"),
    bomVersion: "BOM-FG-001-v2",
    routingVersion: "RTG-FG-001-v1",
    projectCode: "PRJ-001",
    projectName: "Q1 Production",
    warehouseCode: "WH-MAIN",
    warehouseName: "Main Warehouse",
    estimatedCost: 12500.00,
    actualCost: 8125.00,
    currency: "GBP",
    notes: "Priority order for key customer. Ensure quality checks at each stage.",
    materials: [
      {
        id: "m1",
        lineNumber: 1,
        stockItemCode: "RM-001",
        stockItemName: "Raw Material A",
        description: "Primary component",
        quantityRequired: 200,
        quantityIssued: 130,
        quantityOutstanding: 70,
        unitCost: 25.00,
        totalCost: 5000.00,
        warehouseCode: "WH-MAIN",
        warehouseName: "Main Warehouse"
      },
      {
        id: "m2",
        lineNumber: 2,
        stockItemCode: "RM-002",
        stockItemName: "Component B",
        description: "Secondary component",
        quantityRequired: 100,
        quantityIssued: 65,
        quantityOutstanding: 35,
        unitCost: 50.00,
        totalCost: 5000.00,
        warehouseCode: "WH-MAIN",
        warehouseName: "Main Warehouse"
      },
      {
        id: "m3",
        lineNumber: 3,
        stockItemCode: "PKG-001",
        stockItemName: "Packaging Material",
        description: "Standard packaging",
        quantityRequired: 100,
        quantityIssued: 65,
        quantityOutstanding: 35,
        unitCost: 5.00,
        totalCost: 500.00,
        warehouseCode: "WH-MAIN",
        warehouseName: "Main Warehouse"
      }
    ],
    operations: [
      {
        id: "o1",
        operationNumber: 10,
        operationName: "Material Preparation",
        workCenter: "WC-PREP",
        description: "Prepare and stage materials",
        setupTime: 30,
        runTime: 5,
        totalTime: 530,
        status: "completed",
        startDate: new Date("2024-01-15T08:00:00"),
        endDate: new Date("2024-01-15T16:50:00"),
        assignedTo: "John Smith"
      },
      {
        id: "o2",
        operationNumber: 20,
        operationName: "Assembly",
        workCenter: "WC-ASSY",
        description: "Main assembly operation",
        setupTime: 45,
        runTime: 15,
        totalTime: 1545,
        status: "in-progress",
        startDate: new Date("2024-01-16T08:00:00"),
        assignedTo: "Jane Doe"
      },
      {
        id: "o3",
        operationNumber: 30,
        operationName: "Quality Inspection",
        workCenter: "WC-QC",
        description: "Final quality check",
        setupTime: 15,
        runTime: 10,
        totalTime: 1015,
        status: "pending"
      },
      {
        id: "o4",
        operationNumber: 40,
        operationName: "Packaging",
        workCenter: "WC-PACK",
        description: "Package finished goods",
        setupTime: 20,
        runTime: 5,
        totalTime: 520,
        status: "pending"
      }
    ],
    createdBy: "admin",
    createdDate: new Date("2024-01-10"),
    lastModifiedBy: "supervisor",
    lastModifiedDate: new Date("2024-01-20")
  },
  {
    id: "2",
    workOrderNumber: "WO-2024-002",
    productCode: "FG-002",
    productName: "Premium Gadget",
    description: "Custom order for premium gadget",
    quantityOrdered: 50,
    quantityCompleted: 50,
    quantityOutstanding: 0,
    status: "completed",
    priority: "normal",
    startDate: new Date("2024-01-05"),
    dueDate: new Date("2024-01-25"),
    completionDate: new Date("2024-01-24"),
    bomVersion: "BOM-FG-002-v1",
    routingVersion: "RTG-FG-002-v1",
    warehouseCode: "WH-MAIN",
    warehouseName: "Main Warehouse",
    estimatedCost: 8500.00,
    actualCost: 8200.00,
    currency: "GBP",
    notes: "Completed ahead of schedule. Excellent quality.",
    materials: [
      {
        id: "m4",
        lineNumber: 1,
        stockItemCode: "RM-003",
        stockItemName: "Premium Material",
        description: "High-grade component",
        quantityRequired: 100,
        quantityIssued: 100,
        quantityOutstanding: 0,
        unitCost: 75.00,
        totalCost: 7500.00,
        warehouseCode: "WH-MAIN",
        warehouseName: "Main Warehouse"
      }
    ],
    operations: [
      {
        id: "o5",
        operationNumber: 10,
        operationName: "Precision Assembly",
        workCenter: "WC-PREC",
        description: "High-precision assembly",
        setupTime: 60,
        runTime: 20,
        totalTime: 1060,
        status: "completed",
        startDate: new Date("2024-01-05T08:00:00"),
        endDate: new Date("2024-01-24T17:00:00"),
        assignedTo: "Expert Team"
      }
    ],
    createdBy: "admin",
    createdDate: new Date("2024-01-02"),
    lastModifiedBy: "admin",
    lastModifiedDate: new Date("2024-01-24")
  },
  {
    id: "3",
    workOrderNumber: "WO-2024-003",
    productCode: "FG-003",
    productName: "Standard Component Set",
    description: "Bulk production for standard components",
    quantityOrdered: 500,
    quantityCompleted: 0,
    quantityOutstanding: 500,
    status: "planned",
    priority: "normal",
    startDate: new Date("2024-02-01"),
    dueDate: new Date("2024-03-15"),
    bomVersion: "BOM-FG-003-v1",
    routingVersion: "RTG-FG-003-v1",
    projectCode: "PRJ-002",
    projectName: "Q1 Inventory Build",
    warehouseCode: "WH-MAIN",
    warehouseName: "Main Warehouse",
    estimatedCost: 25000.00,
    actualCost: 0,
    currency: "GBP",
    notes: "Scheduled for next month. Ensure materials are ordered in advance.",
    materials: [
      {
        id: "m5",
        lineNumber: 1,
        stockItemCode: "RM-004",
        stockItemName: "Standard Material",
        description: "Bulk material",
        quantityRequired: 1000,
        quantityIssued: 0,
        quantityOutstanding: 1000,
        unitCost: 15.00,
        totalCost: 15000.00,
        warehouseCode: "WH-MAIN",
        warehouseName: "Main Warehouse"
      }
    ],
    operations: [
      {
        id: "o6",
        operationNumber: 10,
        operationName: "Batch Assembly",
        workCenter: "WC-BATCH",
        description: "High-volume assembly",
        setupTime: 120,
        runTime: 3,
        totalTime: 1620,
        status: "pending"
      }
    ],
    createdBy: "planner",
    createdDate: new Date("2024-01-20"),
    lastModifiedBy: "planner",
    lastModifiedDate: new Date("2024-01-20")
  },
  {
    id: "4",
    workOrderNumber: "WO-2024-004",
    productCode: "FG-001",
    productName: "Assembled Widget Pro",
    description: "Urgent replacement order",
    quantityOrdered: 25,
    quantityCompleted: 0,
    quantityOutstanding: 25,
    status: "released",
    priority: "urgent",
    startDate: new Date("2024-01-22"),
    dueDate: new Date("2024-01-29"),
    bomVersion: "BOM-FG-001-v2",
    routingVersion: "RTG-FG-001-v1",
    warehouseCode: "WH-MAIN",
    warehouseName: "Main Warehouse",
    estimatedCost: 3125.00,
    actualCost: 0,
    currency: "GBP",
    notes: "URGENT: Customer replacement order. Expedite all operations.",
    materials: [
      {
        id: "m6",
        lineNumber: 1,
        stockItemCode: "RM-001",
        stockItemName: "Raw Material A",
        description: "Primary component",
        quantityRequired: 50,
        quantityIssued: 0,
        quantityOutstanding: 50,
        unitCost: 25.00,
        totalCost: 1250.00,
        warehouseCode: "WH-MAIN",
        warehouseName: "Main Warehouse"
      }
    ],
    operations: [
      {
        id: "o7",
        operationNumber: 10,
        operationName: "Material Preparation",
        workCenter: "WC-PREP",
        description: "Prepare and stage materials",
        setupTime: 30,
        runTime: 5,
        totalTime: 155,
        status: "pending"
      }
    ],
    createdBy: "supervisor",
    createdDate: new Date("2024-01-22"),
    lastModifiedBy: "supervisor",
    lastModifiedDate: new Date("2024-01-22")
  },
  {
    id: "5",
    workOrderNumber: "WO-2024-005",
    productCode: "FG-004",
    productName: "Custom Assembly",
    description: "Special order - on hold pending customer approval",
    quantityOrdered: 10,
    quantityCompleted: 0,
    quantityOutstanding: 10,
    status: "on-hold",
    priority: "low",
    startDate: new Date("2024-01-18"),
    dueDate: new Date("2024-02-28"),
    bomVersion: "BOM-FG-004-v1",
    routingVersion: "RTG-FG-004-v1",
    warehouseCode: "WH-MAIN",
    warehouseName: "Main Warehouse",
    estimatedCost: 5000.00,
    actualCost: 0,
    currency: "GBP",
    notes: "On hold - awaiting customer design approval before proceeding.",
    materials: [],
    operations: [],
    createdBy: "sales",
    createdDate: new Date("2024-01-18"),
    lastModifiedBy: "sales",
    lastModifiedDate: new Date("2024-01-18")
  }
];

// Status badge styling
const getStatusBadge = (status: WorkOrder["status"]) => {
  const variants: Record<WorkOrder["status"], { variant: "default" | "secondary" | "destructive" | "outline"; className: string; icon: React.ReactNode }> = {
    "planned": { 
      variant: "outline", 
      className: "bg-blue-50 text-blue-700 border-blue-200",
      icon: <Calendar className="h-3 w-3 mr-1" />
    },
    "released": { 
      variant: "outline", 
      className: "bg-purple-50 text-purple-700 border-purple-200",
      icon: <PlayCircle className="h-3 w-3 mr-1" />
    },
    "in-progress": { 
      variant: "outline", 
      className: "bg-yellow-50 text-yellow-700 border-yellow-200",
      icon: <Clock className="h-3 w-3 mr-1" />
    },
    "completed": { 
      variant: "outline", 
      className: "bg-green-50 text-green-700 border-green-200",
      icon: <CheckCircle className="h-3 w-3 mr-1" />
    },
    "on-hold": { 
      variant: "outline", 
      className: "bg-orange-50 text-orange-700 border-orange-200",
      icon: <PauseCircle className="h-3 w-3 mr-1" />
    },
    "cancelled": { 
      variant: "outline", 
      className: "bg-red-50 text-red-700 border-red-200",
      icon: <StopCircle className="h-3 w-3 mr-1" />
    },
  };
  
  const config = variants[status];
  return (
    <Badge variant={config.variant} className={config.className}>
      {config.icon}
      {status.charAt(0).toUpperCase() + status.slice(1).replace("-", " ")}
    </Badge>
  );
};

const getPriorityBadge = (priority: WorkOrder["priority"]) => {
  const variants: Record<WorkOrder["priority"], { variant: "default" | "secondary" | "destructive" | "outline"; className: string }> = {
    "low": { variant: "outline", className: "bg-gray-50 text-gray-600 border-gray-200" },
    "normal": { variant: "outline", className: "bg-blue-50 text-blue-600 border-blue-200" },
    "high": { variant: "outline", className: "bg-orange-50 text-orange-600 border-orange-200" },
    "urgent": { variant: "outline", className: "bg-red-50 text-red-600 border-red-200" },
  };
  
  const config = variants[priority];
  return (
    <Badge variant={config.variant} className={config.className}>
      {priority.charAt(0).toUpperCase() + priority.slice(1)}
    </Badge>
  );
};

const getOperationStatusBadge = (status: WorkOrderOperation["status"]) => {
  const variants: Record<WorkOrderOperation["status"], { variant: "default" | "secondary" | "destructive" | "outline"; className: string }> = {
    "pending": { variant: "outline", className: "bg-gray-50 text-gray-600 border-gray-200" },
    "in-progress": { variant: "outline", className: "bg-yellow-50 text-yellow-700 border-yellow-200" },
    "completed": { variant: "outline", className: "bg-green-50 text-green-700 border-green-200" },
    "skipped": { variant: "outline", className: "bg-orange-50 text-orange-600 border-orange-200" },
  };
  
  const config = variants[status];
  return (
    <Badge variant={config.variant} className={config.className}>
      {status.charAt(0).toUpperCase() + status.slice(1).replace("-", " ")}
    </Badge>
  );
};

export function WorkOrdersPage() {
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>(MOCK_WORK_ORDERS);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<WorkOrder | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"materials" | "operations">("materials");
  const { navigateTo } = useNavigation();
  const { isIntegrated, syncStatus } = useIntegrationStatus();
  
  // Advanced Filters
  const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);
  const [filterRows, setFilterRows] = useState<FilterRow[]>([]);
  const [filterLogic, setFilterLogic] = useState<FilterLogic>("AND");
  const [activeFilters, setActiveFilters] = useState<ActiveFilterChip[]>([]);
  
  // Configure Columns
  const [isConfigureColumnsOpen, setIsConfigureColumnsOpen] = useState(false);
  const [columnConfig, setColumnConfig] = useState<ColumnConfig>({
    visibleColumns: ["workOrderNumber", "productName", "status", "priority", "quantityOrdered", "quantityCompleted", "dueDate", "warehouseName"],
    columnOrder: ["workOrderNumber", "productName", "status", "priority", "quantityOrdered", "quantityCompleted", "dueDate", "warehouseName"]
  });

  // Help System
  const { 
    isHelpOpen, 
    setIsHelpOpen, 
    helpContent 
  } = useContextualHelp("work-orders");

  // Column definitions for Configure Columns
  const columnDefinitions: ColumnDefinition[] = [
    { id: "workOrderNumber", name: "Work Order #", description: "Unique work order identifier" },
    { id: "productName", name: "Product", description: "Product being manufactured" },
    { id: "status", name: "Status", description: "Current work order status" },
    { id: "priority", name: "Priority", description: "Work order priority level" },
    { id: "quantityOrdered", name: "Qty Ordered", description: "Total quantity ordered" },
    { id: "quantityCompleted", name: "Qty Completed", description: "Quantity completed so far" },
    { id: "quantityOutstanding", name: "Qty Outstanding", description: "Remaining quantity to complete" },
    { id: "startDate", name: "Start Date", description: "Work order start date" },
    { id: "dueDate", name: "Due Date", description: "Work order due date" },
    { id: "completionDate", name: "Completion Date", description: "Actual completion date" },
    { id: "warehouseName", name: "Warehouse", description: "Target warehouse" },
    { id: "projectName", name: "Project", description: "Associated project" },
    { id: "estimatedCost", name: "Est. Cost", description: "Estimated cost" },
    { id: "actualCost", name: "Actual Cost", description: "Actual cost incurred" },
    { id: "createdBy", name: "Created By", description: "User who created the work order" },
  ];

  // Filter work orders
  const filteredWorkOrders = useMemo(() => {
    let filtered = workOrders;

    // Apply search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(wo =>
        wo.workOrderNumber.toLowerCase().includes(search) ||
        wo.productCode.toLowerCase().includes(search) ||
        wo.productName.toLowerCase().includes(search) ||
        wo.description.toLowerCase().includes(search) ||
        wo.projectName?.toLowerCase().includes(search)
      );
    }

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(wo => wo.status === statusFilter);
    }

    // Apply priority filter
    if (priorityFilter !== "all") {
      filtered = filtered.filter(wo => wo.priority === priorityFilter);
    }

    // Apply advanced filters
    if (filterRows.length > 0) {
      filtered = applyFilters(filtered, filterRows, filterLogic);
    }

    return filtered;
  }, [workOrders, searchTerm, statusFilter, priorityFilter, filterRows, filterLogic]);

  // Calculate metrics
  const metrics = useMemo(() => {
    const total = workOrders.length;
    const inProgress = workOrders.filter(wo => wo.status === "in-progress").length;
    const completed = workOrders.filter(wo => wo.status === "completed").length;
    const urgent = workOrders.filter(wo => wo.priority === "urgent").length;
    const overdue = workOrders.filter(wo => 
      wo.status !== "completed" && 
      wo.status !== "cancelled" && 
      new Date(wo.dueDate) < new Date()
    ).length;

    return { total, inProgress, completed, urgent, overdue };
  }, [workOrders]);

  const handleViewDetails = (workOrder: WorkOrder) => {
    setSelectedWorkOrder(workOrder);
    setIsDetailsOpen(true);
    setActiveTab("materials");
  };

  const handleApplyAdvancedFilters = (rows: FilterRow[], logic: FilterLogic[]) => {
    setFilterRows(rows);
    setFilterLogic(logic.length > 0 ? logic[0] : "AND");
    const chips = generateFilterChips(rows);
    setActiveFilters(chips);
    setIsAdvancedFiltersOpen(false);
    toast.success(`Applied ${rows.length} filter${rows.length !== 1 ? 's' : ''}`);
  };

  const handleRemoveFilter = (filterId: string) => {
    const updatedRows = filterRows.filter(row => row.id !== filterId);
    setFilterRows(updatedRows);
    const chips = generateFilterChips(updatedRows);
    setActiveFilters(chips);
    toast.success("Filter removed");
  };

  const handleClearAllFilters = () => {
    setFilterRows([]);
    setActiveFilters([]);
    setSearchTerm("");
    setStatusFilter("all");
    setPriorityFilter("all");
    toast.success("All filters cleared");
  };

  const handleExportCSV = () => {
    const data = filteredWorkOrders.map(wo => ({
      "Work Order #": wo.workOrderNumber,
      "Product Code": wo.productCode,
      "Product Name": wo.productName,
      "Status": wo.status,
      "Priority": wo.priority,
      "Qty Ordered": wo.quantityOrdered,
      "Qty Completed": wo.quantityCompleted,
      "Qty Outstanding": wo.quantityOutstanding,
      "Start Date": wo.startDate.toLocaleDateString(),
      "Due Date": wo.dueDate.toLocaleDateString(),
      "Warehouse": wo.warehouseName,
      "Project": wo.projectName || "",
      "Estimated Cost": wo.estimatedCost.toFixed(2),
      "Actual Cost": wo.actualCost.toFixed(2),
    }));
    
    exportToCSV(data, `work-orders-${getCurrentDateForFilename()}`);
    toast.success("Work orders exported to CSV");
  };

  const handleExportExcel = () => {
    const data = filteredWorkOrders.map(wo => ({
      "Work Order #": wo.workOrderNumber,
      "Product Code": wo.productCode,
      "Product Name": wo.productName,
      "Status": wo.status,
      "Priority": wo.priority,
      "Qty Ordered": wo.quantityOrdered,
      "Qty Completed": wo.quantityCompleted,
      "Qty Outstanding": wo.quantityOutstanding,
      "Start Date": wo.startDate.toLocaleDateString(),
      "Due Date": wo.dueDate.toLocaleDateString(),
      "Warehouse": wo.warehouseName,
      "Project": wo.projectName || "",
      "Estimated Cost": wo.estimatedCost.toFixed(2),
      "Actual Cost": wo.actualCost.toFixed(2),
    }));
    
    exportToExcel(data, `work-orders-${getCurrentDateForFilename()}`);
    toast.success("Work orders exported to Excel");
  };

  const handlePrint = () => {
    window.print();
    toast.success("Print dialog opened");
  };

  const handleSync = () => {
    toast.promise(
      new Promise((resolve) => setTimeout(resolve, 2000)),
      {
        loading: "Syncing work orders...",
        success: "Work orders synced successfully",
        error: "Failed to sync work orders",
      }
    );
  };

  const handleStartWorkOrder = (workOrder: WorkOrder) => {
    toast.success(`Started work order ${workOrder.workOrderNumber}`);
    // In a real app, this would update the status to "in-progress"
  };

  const handleCompleteWorkOrder = (workOrder: WorkOrder) => {
    toast.success(`Completed work order ${workOrder.workOrderNumber}`);
    // In a real app, this would update the status to "completed"
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date);
  };

  const isOverdue = (workOrder: WorkOrder) => {
    return workOrder.status !== "completed" && 
           workOrder.status !== "cancelled" && 
           new Date(workOrder.dueDate) < new Date();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">Work Orders</h1>
            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
              🟡 Demo Mode
            </Badge>
            <HelpButton onClick={() => setIsHelpOpen(true)} />
          </div>
          <p className="text-muted-foreground mt-1">
            Manage manufacturing work orders and production schedules
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isIntegrated && (
            <Button variant="outline" size="sm" onClick={handleSync}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Sync
            </Button>
          )}
          <Button onClick={() => toast.info("Create work order feature coming soon")}>
            <Plus className="h-4 w-4 mr-2" />
            Create Work Order
          </Button>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid gap-4 md:grid-cols-5">
        <StaticMetricCardAlt
          title="Total Work Orders"
          value={metrics.total.toString()}
          change="+0%"
          changeLabel="from last month"
          icon={Factory}
          variant="info"
        />
        <StaticMetricCardAlt
          title="In Progress"
          value={metrics.inProgress.toString()}
          change="+0%"
          changeLabel="from last month"
          icon={Clock}
          variant="warning"
        />
        <StaticMetricCardAlt
          title="Completed"
          value={metrics.completed.toString()}
          change="+0%"
          changeLabel="from last month"
          icon={CheckCircle}
          variant="success"
        />
        <StaticMetricCardAlt
          title="Urgent"
          value={metrics.urgent.toString()}
          change="+0%"
          changeLabel="from last month"
          icon={AlertTriangle}
          variant={metrics.urgent > 0 ? "error" : "info"}
        />
        <StaticMetricCardAlt
          title="Overdue"
          value={metrics.overdue.toString()}
          change="+0%"
          changeLabel="from last month"
          icon={AlertTriangle}
          variant={metrics.overdue > 0 ? "error" : "success"}
        />
      </div>

      {/* Filters and Actions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex-1 flex items-center gap-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search work orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="planned">Planned</SelectItem>
                  <SelectItem value="released">Released</SelectItem>
                  <SelectItem value="in-progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="on-hold">On Hold</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Priorities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsAdvancedFiltersOpen(true)}
              >
                <Filter className="h-4 w-4 mr-2" />
                Advanced Filters
                {filterRows.length > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {filterRows.length}
                  </Badge>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsConfigureColumnsOpen(true)}
              >
                <Settings2 className="h-4 w-4 mr-2" />
                Columns
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <Download className="h-4 w-4 mr-2" />
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportExcel}>
                <Download className="h-4 w-4 mr-2" />
                Excel
              </Button>
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
            </div>
          </div>
        </CardHeader>

        {/* Active Filters */}
        {activeFilters.length > 0 && (
          <CardContent className="pt-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {activeFilters.map((filter) => (
                <Badge
                  key={filter.id}
                  variant="secondary"
                  className="gap-1"
                >
                  {filter.label}
                  <X
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => handleRemoveFilter(filter.id)}
                  />
                </Badge>
              ))}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAllFilters}
                className="h-6 px-2 text-xs"
              >
                Clear all
              </Button>
            </div>
          </CardContent>
        )}

        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  {columnConfig.visibleColumns.includes("workOrderNumber") && (
                    <TableHead>Work Order #</TableHead>
                  )}
                  {columnConfig.visibleColumns.includes("productName") && (
                    <TableHead>Product</TableHead>
                  )}
                  {columnConfig.visibleColumns.includes("status") && (
                    <TableHead>Status</TableHead>
                  )}
                  {columnConfig.visibleColumns.includes("priority") && (
                    <TableHead>Priority</TableHead>
                  )}
                  {columnConfig.visibleColumns.includes("quantityOrdered") && (
                    <TableHead className="text-right">Qty Ordered</TableHead>
                  )}
                  {columnConfig.visibleColumns.includes("quantityCompleted") && (
                    <TableHead className="text-right">Qty Completed</TableHead>
                  )}
                  {columnConfig.visibleColumns.includes("quantityOutstanding") && (
                    <TableHead className="text-right">Qty Outstanding</TableHead>
                  )}
                  {columnConfig.visibleColumns.includes("startDate") && (
                    <TableHead>Start Date</TableHead>
                  )}
                  {columnConfig.visibleColumns.includes("dueDate") && (
                    <TableHead>Due Date</TableHead>
                  )}
                  {columnConfig.visibleColumns.includes("completionDate") && (
                    <TableHead>Completion Date</TableHead>
                  )}
                  {columnConfig.visibleColumns.includes("warehouseName") && (
                    <TableHead>Warehouse</TableHead>
                  )}
                  {columnConfig.visibleColumns.includes("projectName") && (
                    <TableHead>Project</TableHead>
                  )}
                  {columnConfig.visibleColumns.includes("estimatedCost") && (
                    <TableHead className="text-right">Est. Cost</TableHead>
                  )}
                  {columnConfig.visibleColumns.includes("actualCost") && (
                    <TableHead className="text-right">Actual Cost</TableHead>
                  )}
                  {columnConfig.visibleColumns.includes("createdBy") && (
                    <TableHead>Created By</TableHead>
                  )}
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredWorkOrders.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columnConfig.visibleColumns.length + 1}
                      className="text-center text-muted-foreground py-8"
                    >
                      No work orders found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredWorkOrders.map((workOrder) => (
                    <TableRow key={workOrder.id} className="cursor-pointer hover:bg-muted/50">
                      {columnConfig.visibleColumns.includes("workOrderNumber") && (
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {workOrder.workOrderNumber}
                            {isOverdue(workOrder) && (
                              <AlertTriangle className="h-4 w-4 text-red-500" />
                            )}
                          </div>
                        </TableCell>
                      )}
                      {columnConfig.visibleColumns.includes("productName") && (
                        <TableCell>
                          <div>
                            <div className="font-medium">{workOrder.productName}</div>
                            <div className="text-sm text-muted-foreground">
                              {workOrder.productCode}
                            </div>
                          </div>
                        </TableCell>
                      )}
                      {columnConfig.visibleColumns.includes("status") && (
                        <TableCell>{getStatusBadge(workOrder.status)}</TableCell>
                      )}
                      {columnConfig.visibleColumns.includes("priority") && (
                        <TableCell>{getPriorityBadge(workOrder.priority)}</TableCell>
                      )}
                      {columnConfig.visibleColumns.includes("quantityOrdered") && (
                        <TableCell className="text-right">
                          {workOrder.quantityOrdered.toLocaleString()}
                        </TableCell>
                      )}
                      {columnConfig.visibleColumns.includes("quantityCompleted") && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {workOrder.quantityCompleted.toLocaleString()}
                            <div className="text-xs text-muted-foreground">
                              ({Math.round((workOrder.quantityCompleted / workOrder.quantityOrdered) * 100)}%)
                            </div>
                          </div>
                        </TableCell>
                      )}
                      {columnConfig.visibleColumns.includes("quantityOutstanding") && (
                        <TableCell className="text-right">
                          {workOrder.quantityOutstanding.toLocaleString()}
                        </TableCell>
                      )}
                      {columnConfig.visibleColumns.includes("startDate") && (
                        <TableCell>{formatDate(workOrder.startDate)}</TableCell>
                      )}
                      {columnConfig.visibleColumns.includes("dueDate") && (
                        <TableCell>
                          <div className={isOverdue(workOrder) ? "text-red-600 font-medium" : ""}>
                            {formatDate(workOrder.dueDate)}
                          </div>
                        </TableCell>
                      )}
                      {columnConfig.visibleColumns.includes("completionDate") && (
                        <TableCell>
                          {workOrder.completionDate ? formatDate(workOrder.completionDate) : "-"}
                        </TableCell>
                      )}
                      {columnConfig.visibleColumns.includes("warehouseName") && (
                        <TableCell>{workOrder.warehouseName}</TableCell>
                      )}
                      {columnConfig.visibleColumns.includes("projectName") && (
                        <TableCell>{workOrder.projectName || "-"}</TableCell>
                      )}
                      {columnConfig.visibleColumns.includes("estimatedCost") && (
                        <TableCell className="text-right">
                          {formatCurrency(workOrder.estimatedCost, workOrder.currency)}
                        </TableCell>
                      )}
                      {columnConfig.visibleColumns.includes("actualCost") && (
                        <TableCell className="text-right">
                          {formatCurrency(workOrder.actualCost, workOrder.currency)}
                        </TableCell>
                      )}
                      {columnConfig.visibleColumns.includes("createdBy") && (
                        <TableCell>{workOrder.createdBy}</TableCell>
                      )}
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(workOrder)}
                        >
                          View Details
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Work Order Details Sheet */}
      <Sheet open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <SheetContent className="sm:max-w-[800px] overflow-y-auto">
          {selectedWorkOrder && (
            <>
              <SheetHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <SheetTitle className="flex items-center gap-2">
                      {selectedWorkOrder.workOrderNumber}
                      {getStatusBadge(selectedWorkOrder.status)}
                      {getPriorityBadge(selectedWorkOrder.priority)}
                    </SheetTitle>
                    <SheetDescription>
                      {selectedWorkOrder.productName} ({selectedWorkOrder.productCode})
                    </SheetDescription>
                  </div>
                  {isIntegrated && (
                    <Button variant="outline" size="sm" asChild>
                      <a href="#" target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View in ERP
                      </a>
                    </Button>
                  )}
                </div>
              </SheetHeader>

              <div className="space-y-6 mt-6">
                {/* Quick Actions */}
                <div className="flex gap-2">
                  {selectedWorkOrder.status === "planned" && (
                    <Button size="sm" onClick={() => handleStartWorkOrder(selectedWorkOrder)}>
                      <PlayCircle className="h-4 w-4 mr-2" />
                      Start Work Order
                    </Button>
                  )}
                  {selectedWorkOrder.status === "in-progress" && (
                    <Button size="sm" onClick={() => handleCompleteWorkOrder(selectedWorkOrder)}>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Complete Work Order
                    </Button>
                  )}
                  <Button variant="outline" size="sm">
                    <Printer className="h-4 w-4 mr-2" />
                    Print
                  </Button>
                </div>

                {/* Overview */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Overview</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-muted-foreground">Description</Label>
                        <p className="text-sm mt-1">{selectedWorkOrder.description}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Warehouse</Label>
                        <p className="text-sm mt-1 flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          {selectedWorkOrder.warehouseName}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-muted-foreground">Quantity Ordered</Label>
                        <p className="text-sm font-medium mt-1">
                          {selectedWorkOrder.quantityOrdered.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Quantity Completed</Label>
                        <p className="text-sm font-medium mt-1">
                          {selectedWorkOrder.quantityCompleted.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Quantity Outstanding</Label>
                        <p className="text-sm font-medium mt-1">
                          {selectedWorkOrder.quantityOutstanding.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-muted-foreground">Start Date</Label>
                        <p className="text-sm mt-1 flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          {formatDate(selectedWorkOrder.startDate)}
                        </p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Due Date</Label>
                        <p className={`text-sm mt-1 flex items-center gap-2 ${isOverdue(selectedWorkOrder) ? "text-red-600 font-medium" : ""}`}>
                          <Calendar className="h-4 w-4" />
                          {formatDate(selectedWorkOrder.dueDate)}
                        </p>
                      </div>
                      {selectedWorkOrder.completionDate && (
                        <div>
                          <Label className="text-muted-foreground">Completion Date</Label>
                          <p className="text-sm mt-1 flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            {formatDate(selectedWorkOrder.completionDate)}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-muted-foreground">Estimated Cost</Label>
                        <p className="text-sm font-medium mt-1 flex items-center gap-2">
                          <DollarSign className="h-4 w-4" />
                          {formatCurrency(selectedWorkOrder.estimatedCost, selectedWorkOrder.currency)}
                        </p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Actual Cost</Label>
                        <p className="text-sm font-medium mt-1 flex items-center gap-2">
                          <DollarSign className="h-4 w-4" />
                          {formatCurrency(selectedWorkOrder.actualCost, selectedWorkOrder.currency)}
                        </p>
                      </div>
                    </div>
                    {selectedWorkOrder.projectName && (
                      <div>
                        <Label className="text-muted-foreground">Project</Label>
                        <p className="text-sm mt-1">
                          {selectedWorkOrder.projectName} ({selectedWorkOrder.projectCode})
                        </p>
                      </div>
                    )}
                    <div>
                      <Label className="text-muted-foreground">BOM Version</Label>
                      <p className="text-sm mt-1">{selectedWorkOrder.bomVersion}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Routing Version</Label>
                      <p className="text-sm mt-1">{selectedWorkOrder.routingVersion}</p>
                    </div>
                    {selectedWorkOrder.notes && (
                      <div>
                        <Label className="text-muted-foreground">Notes</Label>
                        <p className="text-sm mt-1">{selectedWorkOrder.notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Tabs for Materials and Operations */}
                <div className="space-y-4">
                  <div className="flex gap-2 border-b">
                    <Button
                      variant={activeTab === "materials" ? "default" : "ghost"}
                      size="sm"
                      onClick={() => setActiveTab("materials")}
                      className="rounded-b-none"
                    >
                      <Package2 className="h-4 w-4 mr-2" />
                      Materials ({selectedWorkOrder.materials.length})
                    </Button>
                    <Button
                      variant={activeTab === "operations" ? "default" : "ghost"}
                      size="sm"
                      onClick={() => setActiveTab("operations")}
                      className="rounded-b-none"
                    >
                      <Wrench className="h-4 w-4 mr-2" />
                      Operations ({selectedWorkOrder.operations.length})
                    </Button>
                  </div>

                  {activeTab === "materials" && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Required Materials</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="rounded-md border">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Line</TableHead>
                                <TableHead>Stock Item</TableHead>
                                <TableHead className="text-right">Required</TableHead>
                                <TableHead className="text-right">Issued</TableHead>
                                <TableHead className="text-right">Outstanding</TableHead>
                                <TableHead className="text-right">Unit Cost</TableHead>
                                <TableHead className="text-right">Total Cost</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {selectedWorkOrder.materials.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={7} className="text-center text-muted-foreground py-4">
                                    No materials defined
                                  </TableCell>
                                </TableRow>
                              ) : (
                                selectedWorkOrder.materials.map((material) => (
                                  <TableRow key={material.id}>
                                    <TableCell>{material.lineNumber}</TableCell>
                                    <TableCell>
                                      <div>
                                        <div className="font-medium">{material.stockItemName}</div>
                                        <div className="text-sm text-muted-foreground">
                                          {material.stockItemCode}
                                        </div>
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {material.quantityRequired.toLocaleString()}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {material.quantityIssued.toLocaleString()}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {material.quantityOutstanding.toLocaleString()}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {formatCurrency(material.unitCost, selectedWorkOrder.currency)}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {formatCurrency(material.totalCost, selectedWorkOrder.currency)}
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {activeTab === "operations" && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Operations</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="rounded-md border">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Op #</TableHead>
                                <TableHead>Operation</TableHead>
                                <TableHead>Work Center</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Setup (min)</TableHead>
                                <TableHead className="text-right">Run (min/unit)</TableHead>
                                <TableHead className="text-right">Total (min)</TableHead>
                                <TableHead>Assigned To</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {selectedWorkOrder.operations.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={8} className="text-center text-muted-foreground py-4">
                                    No operations defined
                                  </TableCell>
                                </TableRow>
                              ) : (
                                selectedWorkOrder.operations.map((operation) => (
                                  <TableRow key={operation.id}>
                                    <TableCell>{operation.operationNumber}</TableCell>
                                    <TableCell>
                                      <div>
                                        <div className="font-medium">{operation.operationName}</div>
                                        <div className="text-sm text-muted-foreground">
                                          {operation.description}
                                        </div>
                                      </div>
                                    </TableCell>
                                    <TableCell>{operation.workCenter}</TableCell>
                                    <TableCell>{getOperationStatusBadge(operation.status)}</TableCell>
                                    <TableCell className="text-right">{operation.setupTime}</TableCell>
                                    <TableCell className="text-right">{operation.runTime}</TableCell>
                                    <TableCell className="text-right">{operation.totalTime}</TableCell>
                                    <TableCell>{operation.assignedTo || "-"}</TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Metadata */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Metadata</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Created By:</span>
                      <span className="font-medium">{selectedWorkOrder.createdBy}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Created Date:</span>
                      <span>{formatDate(selectedWorkOrder.createdDate)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Last Modified By:</span>
                      <span className="font-medium">{selectedWorkOrder.lastModifiedBy}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Last Modified Date:</span>
                      <span>{formatDate(selectedWorkOrder.lastModifiedDate)}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Advanced Filters Modal */}
      <AdvancedFiltersModal
        open={isAdvancedFiltersOpen}
        onOpenChange={setIsAdvancedFiltersOpen}
        onApply={handleApplyAdvancedFilters}
        initialFilters={filterRows}
        initialLogic={filterRows.length > 1 ? Array(filterRows.length - 1).fill(filterLogic) : []}
        entityType="stockItems"
      />

      {/* Configure Columns Modal */}
      <ConfigureColumnsModal
        open={isConfigureColumnsOpen}
        onOpenChange={setIsConfigureColumnsOpen}
        columns={columnDefinitions}
        currentConfig={columnConfig}
        defaultConfig={{
          visibleColumns: ["workOrderNumber", "productName", "status", "priority", "quantityOrdered", "quantityCompleted", "dueDate", "warehouseName"],
          columnOrder: ["workOrderNumber", "productName", "status", "priority", "quantityOrdered", "quantityCompleted", "dueDate", "warehouseName"]
        }}
        onSave={(config) => {
          setColumnConfig(config);
          setIsConfigureColumnsOpen(false);
          toast.success("Column configuration saved");
        }}
      />

      {/* Contextual Help Drawer */}
      <ContextualHelpDrawer
        open={isHelpOpen}
        onOpenChange={setIsHelpOpen}
        title={helpContent.title}
        content={helpContent.content}
      />
    </div>
  );
}
